import React, { useState } from 'react';
import { Radar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useData } from '../context/DataContext';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ChartDataLabels);

const RotacionPersonalBar = ({ selectedIndustries = [], onBarClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { getForecastAccuracy, dataReady } = useData();

  // Get data from Excel (3 years: 2023, 2024, 2025)
  const excelData = dataReady ? getForecastAccuracy() : { labels: [], data2023: [], data2024: [], data2025: [] };

  // Use Excel data or fallback
  const allLabels = excelData.labels.length > 0
    ? excelData.labels
    : ['Automotriz', 'Manufactura', 'Alimentos', 'Construcción', 'Retail', 'Servicios Logísticos', 'Sector salud/ Farmacéuticas'];

  const allData2023 = excelData.data2023.length > 0 ? excelData.data2023 : [90, 84, 81, 78, 72, 73, 0];
  const allData2024 = excelData.data2024.length > 0 ? excelData.data2024 : [94, 81, 87, 83, 82, 75, 86];
  const allData2025 = (excelData.data2025 && excelData.data2025.length > 0) ? excelData.data2025 : [84, 81, 81, 82, 79, 83, 82];

  const labels = selectedIndustries.length > 0 ? selectedIndustries : allLabels;

  const getValuesForLabels = (allValues) => labels.map((label) => {
    const index = allLabels.indexOf(label);
    return index !== -1 ? allValues[index] : 0;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: '2023',
        data: getValuesForLabels(allData2023),
        backgroundColor: 'rgba(162, 174, 187, 0.2)',
        borderColor: '#A2AEBB',
        pointBackgroundColor: '#A2AEBB',
        borderWidth: 2,
        pointRadius: 4,
      },
      {
        label: '2024',
        data: getValuesForLabels(allData2024),
        backgroundColor: 'rgba(71, 106, 142, 0.2)',
        borderColor: '#476A8E',
        pointBackgroundColor: '#476A8E',
        borderWidth: 2,
        pointRadius: 4,
      },
      {
        label: '2025',
        data: getValuesForLabels(allData2025),
        backgroundColor: 'rgba(31, 61, 88, 0.2)',
        borderColor: '#1F3D58',
        pointBackgroundColor: '#1F3D58',
        borderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          font: { size: 12 },
          callback: (value) => `${value}%`,
        },
        pointLabels: {
          font: { size: 12 },
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 14 },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw}%`;
          },
        },
      },
      datalabels: {
        display: true,
        color: '#000',
        font: {
          weight: 'bold',
          size: 10,
        },
        formatter: (value) => `${value}%`,
        anchor: 'end',
        align: (context) => {
          // Stagger alignment per dataset so the three years don't overlap
          const i = context.datasetIndex;
          return i === 0 ? 'start' : i === 1 ? 'center' : 'end';
        },
        offset: (context) => {
          const i = context.datasetIndex;
          return i === 0 ? 4 : i === 1 ? 10 : 16;
        },
      },
    },
  };

  const infoContent = {
    formula: 'Precisión de pronóstico (%) = 100% − [(Σ Valor Absoluto ((Demanda Pronosticada − Demanda Real) / Demanda Real) ÷ Venta Bruta) × 100]',
    description: 'La precisión del pronóstico es un indicador logístico clave que mide qué tan acertadas son las estimaciones de demanda en comparación con la demanda real.',
  };

  return (
    <div style={{ maxWidth: '800px', height: '500px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={-3}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <div style={{ width: '100%', height: '450px' }}>
        <Radar data={chartData} options={options} />
      </div>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RotacionPersonalBar;
